// ----------------------------------------------------------------------
// <copyright file="Izenda.FiltterValue.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import connect from 'share/enhancers/Izenda.Connect';
import React, { Component } from 'react';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import LabelDropDownList from './Izenda.LabelDropDownList';
import Select from 'share/components/controls/multiSelectBox/Izenda.Select';
import PropTypes from 'prop-types';
import { FILTER_MODE } from 'logic/data/Izenda.FilterLookupConstants';

@connect()
export default class FilterValue extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired
  };

  componentDidMount() {
    const { store } = this.props;
    store.getDatabaseList();
    store.getSystemVariables();
  }

  /**
   * Update filter mode
   * @param {Event} event Change option event
   */
  updateFilterMode = (event: Event) => {
    const {
      target: { value }
    } = event;
    const { store } = this.props;
    store.updateFilterMode(value);
  };

  /**
   * On closing popup
   * @param {Function} close Close popup function
   * @param {string} type Close action
   */
  onClosingPopup = (close: Function, type: string) => {
    const {
      props: { store }
    } = this;
    if (type === 'accept') {
      this.props.validation.validate().then((success: boolean) => {
        if (success) {
          store.saveFillerValue();
          close();
          store.resetPopup();
        }
      });
    } else if (type === 'cancel') {
      close();
      store.resetPopup();
    }
  };

  /**
   * On change DB
   * @param {Object} newValue New DB
   */
  onChangeDbName = (newValue: any) => {
    this.props.store
      .setDbName({ key: newValue.value, value: newValue.label })
      .then(() => this.props.validation.validate());
  };

  /**
   * On change table
   * @param {Object} newValue New table
   */
  onChangeTable = (newValue: any) => {
    this.props.store
      .setTable({ key: newValue.value, value: newValue.label })
      .then(() => this.props.validation.validate());
  };

  /**
   * On change lookup
   * @param {Object} newValue New lookup key
   */
  onChangeLookupKey = (newValue: any) => {
    this.props.store.setLookupKey({ key: newValue.value, value: newValue.label });
  };

  /**
   * On change display value
   * @param {Object} newValue New display value
   */
  onChangeDisplayValue = (newValue: any) => {
    this.props.store.setDisplayValue({ key: newValue.value, value: newValue.label });
  };

  /**
   * On change user defined value
   * @param {Object} value value
   */
  onChangeUserDefinedFilterValue = (value: any) => {
    this.props.store.setUserDefinedFilterValue(value);
  };

  /**
   * On add new user defined value
   * @param {Object} newOption New value
   */
  onAddNewUserDefinedFilterValue = (newOption: any) => {
    this.props.store.addNewFilterValue(newOption);
  };

  /**
   * Render content in DB lookup mode
   */
  renderFilterByKey() {
    const {
      store,
      store: { dbStructureValue },
      localization
    } = this.props;

    return (
      <section className='form-horizontal'>
        {/* database name */}
        <LabelDropDownList
          options={store.connectionList}
          onChange={this.onChangeDbName}
          value={dbStructureValue.database.key}
          label={localization.getText('COMMON_LABEL_DATABASE_NAME')}
        />

        {/* table name */}
        <LabelDropDownList
          options={store.tableList}
          onChange={this.onChangeTable}
          value={dbStructureValue.table.key}
          label={localization.getText('COMMON_LABEL_TABLE_NAME')}
        />

        {/* lookup key */}
        <LabelDropDownList
          options={store.fieldList}
          onChange={this.onChangeLookupKey}
          value={dbStructureValue.lookupKey.key}
          label={localization.getText('SETTINGS_LABEL_LOOKUP_KEY')}
        />

        {/* display value */}
        <LabelDropDownList
          options={store.fieldList}
          onChange={this.onChangeDisplayValue}
          value={dbStructureValue.displayValue.key}
          label={localization.getText('SETTINGS_LABEL_DISPLAY_VALUE')}
        />
      </section>
    );
  }

  /**
   * Render content in user defined mode
   */
  renderDefinedFilterValue() {
    const { localization, store } = this.props;
    return (
      <section>
        <Select
          multi
          simpleValue
          options={this.props.store.userDefinedOptions}
          labelKey='name'
          valueKey='id'
          value={store.userDefinedValue}
          noResultsText={localization.getText(
            'QUERY_SOURCE_TYPE_CONFIG_LABEL_FILTER_VALUE_ENTER_TO_CREATE_NEW_FILTER_VALUE'
          )}
          onChange={this.onChangeUserDefinedFilterValue}
          onAddNew={this.onAddNewUserDefinedFilterValue}
          scalable={false}
        />
      </section>
    );
  }

  /**
   * Render control by mode
   * @param {number} filterMode The field filter mode
   */
  renderControlByMode(filterMode: number) {
    const { store } = this.props;

    return (
      filterMode === store.filterMode &&
      (filterMode === FILTER_MODE.DbStructure ? this.renderFilterByKey() : this.renderDefinedFilterValue())
    );
  }

  /**
   * Render filter value popup content
   * @param {string} groupName
   * @param {string} filterMode
   * @param {string} title
   */
  renderFilterContent(groupName: string, filterMode: string, title: string) {
    const { store } = this.props;
    return (
      <div className='radio'>
        {/* radio control */}
        <label>
          <input
            type='radio'
            name={groupName}
            value={filterMode}
            checked={store.filterMode === filterMode}
            onChange={this.updateFilterMode}
          />
          {title}
        </label>
        {/* control for fillter mode */}
        {this.renderControlByMode(filterMode)}
      </div>
    );
  }

  render() {
    const { store, localization } = this.props;

    return (
      <Popup type='none' visible={store.showPopup} autoClose={false} onClosing={this.onClosingPopup}>
        <Popup.Header title={localization.getText('SETTINGS_LABEL_FILTER_VALUE')} />
        <Popup.Body>
          <form>
            {this.renderFilterContent(
              'optradio',
              FILTER_MODE.DbStructure,
              localization.getText('SETTINGS_LABEL_FILTER_LOOKUP')
            )}
            {this.renderFilterContent(
              'optradio',
              FILTER_MODE.UserDefined,
              localization.getText('SETTINGS_LABEL_USER_DEFINE')
            )}
          </form>
        </Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type='accept'>{localization.getText('COMMON_LABEL_OK')}</Popup.ConfirmationButton>
          <Popup.ConfirmationButton type='cancel'>
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/DataModel/QuerySourceType/FilterValue/Izenda.FilterValue.js